import {
  type GenerateOtterSubscriptionEntitiesError,
  isUserEligibleToUpgrade,
  type Plan,
  validateEmailDomain,
} from '@otter-ai/recall-client';
import {
  type ErrorType,
  PurchaseCheckUserEligibilityEvent,
  PurchaseFailEvent,
  PurchaseGenerateOtterSubscriptionEntitiesEvent,
  PurchaseStripeConfirmationEvent,
  trackEvent,
} from '@otter-ai/telemetry';
import {
  AmplitudeEventType,
  handleMissingStripeIdError,
  isMissingStripeIdError,
  type SessionInput,
  usePurchaseAnalytics,
} from '@otter-ai/zone-purchase/src/lib/DO_NOT_USE';
import { useCheckout } from '@stripe/react-stripe-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useGenerateSubscriptionEntitiesMutation } from '@/query/mutations/useGenerateSubscriptionEntitiesMutation';
import { subscriptionQuery } from '@/query/subscriptionQueryCreator';
import { userProfileQueryCreator } from '@/query/userProfileQueryCreator';
import { workspaceQueryCreator } from '@/query/workspaceQueryCreator';
import { useWorkspace } from '@/shared/hooks/useWorkspace';
import { checkoutTrackingAtom } from '@/shared/modals/cc-trial-modal/utils/atoms';
import { getBrowserName } from '@/shared/utils/utilityFunctions';

interface UseCompleteCheckoutProps {
  onCloseDialog?: () => void;
  defaultPaymentMethodId?: string;
  selectedPlan: Plan | undefined;
  onError: (errorMessage: string) => void;
  onPurchaseComplete: () => void;
  checkoutType: 'reactivate' | 'upgrade';
  setPurchaseInProgress: (purchaseInProgress: boolean) => void;
}

export function useCompleteCheckout({
  onCloseDialog,
  defaultPaymentMethodId,
  selectedPlan,
  onError,
  onPurchaseComplete,
  checkoutType,
  setPurchaseInProgress,
}: UseCompleteCheckoutProps) {
  const {
    confirm,
    id,
    total,
    minorUnitsAmountDivisor,
    lineItems,
    discountAmounts,
    recurring,
    shippingAddress,
  } = useCheckout();
  const { logPurchaseSuccessEvent } = usePurchaseAnalytics({
    selectedPlanParam: selectedPlan,
    planTypeParam: selectedPlan?.type,
  });
  const { workspace, adminStatus } = useWorkspace();
  const { data: profileData } = useQuery(userProfileQueryCreator());
  const queryClient = useQueryClient();
  const checkoutTracking = useAtomValue(checkoutTrackingAtom);
  const generateSubscriptionEntities = useGenerateSubscriptionEntitiesMutation({
    onSuccess: async () => {
      trackEvent(
        PurchaseGenerateOtterSubscriptionEntitiesEvent({
          Status: 'success',
          CheckoutType: checkoutType,
        }),
      );

      try {
        const isBasicWorkspace = workspace?.workspace_type === 'basic';
        const isBasicAdmin = Boolean(isBasicWorkspace && adminStatus?.isAdmin);
        const isBasicMember = Boolean(
          isBasicWorkspace && !adminStatus?.isAdmin,
        );

        await queryClient.invalidateQueries({
          queryKey: subscriptionQuery().queryKey,
        });

        const subscription = await queryClient.fetchQuery(subscriptionQuery());

        queryClient.invalidateQueries({
          queryKey: workspaceQueryCreator().queryKey,
        });

        const session: SessionInput = {
          total,
          minorUnitsAmountDivisor,
          lineItems,
          discountAmounts,
          recurring,
          shippingAddress,
        };

        logPurchaseSuccessEvent({
          session,
          userData: profileData?.user,
          usedSavedPayment: Boolean(defaultPaymentMethodId),
          isPaymentRequired: true,
          subscription,
          isBasicWorkspace,
          isBasicAdmin,
          isBasicMember,
          checkoutType,
          premiumFeature: checkoutTracking.premiumFeature,
          checkoutEntryPoint: checkoutTracking.checkoutEntryPoint,
        });
      } catch (error) {
        console.error('Error logging purchase success event:', error);
      }

      onPurchaseComplete();
      onCloseDialog?.();
    },
    onError: (error: GenerateOtterSubscriptionEntitiesError) => {
      if (isMissingStripeIdError(error)) {
        handleMissingStripeIdError(error);
        trackEvent(
          PurchaseGenerateOtterSubscriptionEntitiesEvent({
            Status: 'success',
            CheckoutType: checkoutType,
          }),
        );
      } else {
        trackEvent(
          PurchaseGenerateOtterSubscriptionEntitiesEvent({
            Status: 'failure',
            ErrorMessage: error.message,
            CheckoutType: checkoutType,
          }),
        );
        onError?.(error.message ?? 'Error generating subscription');
      }
    },
  });

  const isEduPlan =
    selectedPlan?.discount_type === 'academic' ||
    selectedPlan?.discount_type === 'education';

  const handleCompleteCheckout = async () => {
    setPurchaseInProgress(true);
    try {
      if (isEduPlan) {
        const response = await validateEmailDomain();
        if (response.data?.status === 'OK' && !response.data?.is_student) {
          throw new Error('Invalid educational email');
        }
        if (response.error) {
          throw new Error(response.error.message);
        }
      }
      // Step 1: Check user eligibility for purchase
      try {
        const { data: eligibilityData } = await isUserEligibleToUpgrade({
          query: {
            plan_id: selectedPlan?.id.toString() ?? '',
            checkout_session_id: id,
          },
          throwOnError: true,
        });

        if (!eligibilityData?.is_eligible) {
          const errorMessage =
            eligibilityData?.message || 'Unable to complete purchase';
          trackEvent(
            PurchaseCheckUserEligibilityEvent({
              Status: 'failure',
              ErrorMessage: errorMessage,
            }),
          );
          onError('Unable to complete the purchase.');
          return;
        }

        trackEvent(
          PurchaseCheckUserEligibilityEvent({
            Status: 'success',
            CheckoutType: checkoutType,
          }),
        );
      } catch (eligibilityError) {
        const errorMessage =
          eligibilityError instanceof Error
            ? eligibilityError.message
            : 'Unable to complete purchase';
        trackEvent(
          PurchaseCheckUserEligibilityEvent({
            Status: 'failure',
            ErrorMessage: errorMessage,
            CheckoutType: checkoutType,
          }),
        );
        onError('Unable to complete the purchase.');
        return;
      }

      // Step 2: Confirm payment with Stripe
      const confirmResult = await confirm({
        paymentMethod: defaultPaymentMethodId,
        redirect: 'if_required',
      });

      if (confirmResult.type === 'success') {
        trackEvent(
          PurchaseStripeConfirmationEvent({
            Status: 'success',
            CheckoutType: checkoutType,
          }),
        );

        // Step 3: Generate subscription entities
        await generateSubscriptionEntities.mutateAsync({
          checkout_session_id: id,
        });
      } else {
        trackEvent(
          PurchaseStripeConfirmationEvent({
            Status: 'failure',
            ErrorMessage: confirmResult.error.message,
            CheckoutType: checkoutType,
          }),
        );
        throw new Error(confirmResult.error.message);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Payment failed';
      trackEvent(
        PurchaseFailEvent({
          PlanType: selectedPlan?.type,
          SeatCount: lineItems[0].quantity,
          ErrorMessage: errorMessage,
          CheckoutType: checkoutType,
        }),
      );
      trackEvent(AmplitudeEventType.Purchase_FailureEvent, {
        ErrorType: errorMessage as ErrorType,
        CheckoutType: checkoutType,
        BrowserType: getBrowserName(navigator.userAgent),
      });
      onError(errorMessage);
    } finally {
      setPurchaseInProgress(false);
      onCloseDialog?.();
    }
  };

  return {
    handleCompleteCheckout,
    isGeneratingSubscription: generateSubscriptionEntities.isPending,
  };
}
